// Ticket detail panel with transition controls + R4 calendar linking.

import React, { useCallback, useEffect, useState } from "react";
import { useLang } from "@/i18n";
import { api, extractErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CalendarClock, CalendarPlus, Link2Off } from "lucide-react";
import { StatusBadge, friendlyStatus } from "./TicketStatusBits";
import SchedulingDialog from "./SchedulingDialog";
import ContextCard from "./ContextCard";

// Forward transitions matching backend TICKET_TRANSITIONS.
const NEXT_TRANSITIONS = {
  requested:        [{ to: "accepted", labelKey: "transitionAccept" }],
  accepted:         [{ to: "in_progress", labelKey: "transitionStart" }],
  in_progress:      [{ to: "ready_for_pickup", labelKey: "transitionReady" }],
  ready_for_pickup: [{ to: "delivered", labelKey: "transitionDeliver" }],
  delivered:        [],
  cancelled:        [],
};
const TERMINAL = new Set(["delivered", "cancelled"]);
const ADMIN_ROLES = new Set(["admin", "staff"]);

// Render an ISO datetime as "YYYY-MM-DD HH:mm" in the user's local time.
const _fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
};

export default function TicketDetailPanel({ ticketId, currentUser, onUpdated, onClose }) {
  const { t } = useLang();
  const [ticket, setTicket] = useState(null);
  const [calendarEvent, setCalendarEvent] = useState(null);
  const [playerContext, setPlayerContext] = useState(null);
  const [technicianContext, setTechnicianContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/racket-lab/service-tickets/${ticketId}/with-context`);
      setTicket(r.data?.ticket || null);
      setCalendarEvent(r.data?.calendar_event || null);
      setPlayerContext(r.data?.player_context || null);
      setTechnicianContext(r.data?.technician_context || null);
    } catch {
      toast.error(t("racketLab.tickets.updateFail"));
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [ticketId, t, onClose]);

  useEffect(() => { load(); }, [load]);

  const canMutate = ticket && (
    ADMIN_ROLES.has(currentUser?.role) ||
    ticket.technician_id === currentUser?.id
  );

  const transition = async (next) => {
    setBusy(true);
    try {
      await api.patch(`/racket-lab/service-tickets/${ticketId}`, { status: next });
      toast.success(t("racketLab.tickets.updateOk"));
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("racketLab.tickets.updateFail"));
    } finally {
      setBusy(false);
    }
  };

  const handleLinked = async () => {
    await load();
    onUpdated?.();
  };

  const handleUnlink = async () => {
    setBusy(true);
    try {
      await api.delete(`/racket-lab/service-tickets/${ticketId}/calendar`);
      toast.success(t("racketLab.tickets.schedulingUnlinkOk"));
      setUnlinkOpen(false);
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("racketLab.tickets.schedulingUnlinkFail")));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">…</div>
    );
  }
  if (!ticket) return null;

  const fwd = NEXT_TRANSITIONS[ticket.status] || [];
  const showCancel = !TERMINAL.has(ticket.status);
  const ticketTerminal = TERMINAL.has(ticket.status);
  const canCreateEvent = canMutate && !ticketTerminal && !calendarEvent;
  const canUnlinkEvent = canMutate && !!calendarEvent;

  return (
    <div className="space-y-4" data-testid="racket-lab-tickets-detail">
      {/* Header */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs font-mono text-slate-500" data-testid="racket-lab-tickets-detail-id">
              {ticket.id}
            </div>
            <div className="text-lg font-semibold text-slate-900 mt-1">
              {ticket.racket_model || "—"}
              {ticket.sport && <span className="text-slate-500 font-normal text-sm ms-2">· {ticket.sport}</span>}
            </div>
            {ticket.notes && (
              <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{ticket.notes}</p>
            )}
          </div>
          <StatusBadge status={ticket.status} t={t} />
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-slate-500">{t("racketLab.tickets.fieldTechnician")}</div>
            <div className="font-mono text-slate-700 truncate">{ticket.technician_id}</div>
          </div>
          <div>
            <div className="text-slate-500">{t("racketLab.tickets.fieldPlayer")}</div>
            <div className="font-mono text-slate-700 truncate">{ticket.player_id}</div>
          </div>
          {ticket.string_job_ids?.length > 0 && (
            <div>
              <div className="text-slate-500">{t("racketLab.tickets.fieldStringJobs")}</div>
              <div className="font-mono text-slate-700">{ticket.string_job_ids.length}</div>
            </div>
          )}
        </div>

        {/* RL-N5 (May 2026) — Customer "ready for pickup" notification badge.
            Surfaces the outcome of the automatic email sent the first time
            this ticket reached `ready_for_pickup`. Tone per status:
              • sent / mocked → emerald (success)
              • failed        → rose (action needed)
              • no_email      → amber (informational, manual action)
            Hidden entirely when no notification has been attempted yet. */}
        {ticket.ready_notification_sent_at && (
          <div
            className={
              "mt-3 rounded-lg border px-3 py-2 text-xs flex items-start gap-2 " +
              (ticket.ready_notification_status === "sent" || ticket.ready_notification_status === "mocked"
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : ticket.ready_notification_status === "failed"
                ? "border-rose-200 bg-rose-50 text-rose-900"
                : "border-amber-200 bg-amber-50 text-amber-900")
            }
            data-testid="rln5-ready-notification-badge"
          >
            <span className="font-semibold whitespace-nowrap">
              {ticket.ready_notification_status === "sent" || ticket.ready_notification_status === "mocked"
                ? t("racketLab.tickets.rln5Sent")
                : ticket.ready_notification_status === "failed"
                ? t("racketLab.tickets.rln5Failed")
                : t("racketLab.tickets.rln5NoEmail")}
            </span>
            <span className="text-slate-500">·</span>
            <span>{_fmt(ticket.ready_notification_sent_at)}</span>
          </div>
        )}
      </div>

      {/* R4.1-A — Context cards (mirror layer over TEAM OS Shared Core) */}
      {(playerContext || technicianContext) && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          data-testid="racket-lab-tickets-detail-context-grid"
        >
          <ContextCard
            role="player"
            context={playerContext}
            t={t}
            isAdmin={ADMIN_ROLES.has(currentUser?.role)}
          />
          <ContextCard
            role="technician"
            context={technicianContext}
            t={t}
            isAdmin={ADMIN_ROLES.has(currentUser?.role)}
          />
        </div>
      )}

      {/* Scheduling (R4) */}
      <div
        className="rounded-xl border border-slate-200 bg-white p-5"
        data-testid="racket-lab-tickets-detail-scheduling"
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-violet-600" />
            {t("racketLab.tickets.schedulingTitle")}
          </div>
          {canCreateEvent && (
            <Button
              onClick={() => setSchedOpen(true)}
              size="sm"
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="racket-lab-tickets-detail-scheduling-create"
            >
              <CalendarPlus className="h-3.5 w-3.5 me-1.5" />
              {t("racketLab.tickets.schedulingCreateCta")}
            </Button>
          )}
        </div>

        {calendarEvent ? (
          <div className="space-y-2" data-testid="racket-lab-tickets-detail-scheduling-event">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-slate-500">{t("racketLab.tickets.schedulingEventWindow")}</div>
                <div className="text-slate-800" data-testid="racket-lab-tickets-detail-scheduling-window">
                  {_fmt(calendarEvent.starts_at)} → {_fmt(calendarEvent.ends_at)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">{t("racketLab.tickets.schedulingEventLocation")}</div>
                <div className="text-slate-800">{calendarEvent.location || "—"}</div>
              </div>
              <div>
                <div className="text-slate-500">{t("racketLab.tickets.schedulingEventStatus")}</div>
                <div>
                  <span className="inline-flex items-center rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-200 px-2 py-0.5 text-[11px] font-medium">
                    {t("racketLab.tickets.schedulingEventActive")}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-slate-500">{t("racketLab.tickets.schedulingEventRef")}</div>
                <div className="font-mono text-slate-700 truncate" title={calendarEvent.id}>
                  {calendarEvent.id}
                </div>
              </div>
            </div>
            {canUnlinkEvent && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUnlinkOpen(true)}
                  disabled={busy}
                  className="text-rose-700 border-rose-200 hover:bg-rose-50"
                  data-testid="racket-lab-tickets-detail-scheduling-unlink"
                >
                  <Link2Off className="h-3.5 w-3.5 me-1.5" />
                  {t("racketLab.tickets.schedulingUnlink")}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p
            className="text-xs text-slate-500"
            data-testid="racket-lab-tickets-detail-scheduling-empty"
          >
            {canMutate
              ? t("racketLab.tickets.schedulingNoneTech")
              : t("racketLab.tickets.schedulingNonePlayer")}
          </p>
        )}

        {!canMutate && (
          <p
            className="text-[11px] text-slate-400 mt-2 italic"
            data-testid="racket-lab-tickets-detail-scheduling-player-note"
          >
            {t("racketLab.tickets.schedulingPlayerNote")}
          </p>
        )}
      </div>

      {/* Transitions */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="racket-lab-tickets-detail-transitions">
        <div className="text-sm font-semibold text-slate-900 mb-3">
          {t("racketLab.tickets.transitionTitle")}
        </div>
        {!canMutate ? (
          <p className="text-xs text-slate-500" data-testid="racket-lab-tickets-detail-readonly">
            {t("racketLab.tickets.transitionPlayerNote")}
          </p>
        ) : TERMINAL.has(ticket.status) ? (
          <p className="text-xs text-slate-500" data-testid="racket-lab-tickets-detail-terminal">
            {t("racketLab.tickets.transitionTerminal")}
          </p>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {fwd.map(({ to, labelKey }) => (
              <Button
                key={to}
                onClick={() => transition(to)}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`racket-lab-tickets-detail-transition-${to}`}
              >
                {t(`racketLab.tickets.${labelKey}`)}
              </Button>
            ))}
            {showCancel && (
              <Button
                variant="outline"
                onClick={() => transition("cancelled")}
                disabled={busy}
                data-testid="racket-lab-tickets-detail-transition-cancelled"
              >
                {t("racketLab.tickets.transitionCancel")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* History */}
      {ticket.status_history?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="racket-lab-tickets-detail-history">
          <div className="text-sm font-semibold text-slate-900 mb-3">
            {t("racketLab.tickets.fieldHistory")}
          </div>
          <ol className="space-y-1.5">
            {ticket.status_history.map((h, i) => (
              <li key={i} className="text-xs text-slate-700 flex items-center gap-2">
                <span className="font-mono text-slate-400">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                <span>
                  {h.from ? friendlyStatus(h.from, t) : "—"}
                  <span className="mx-1 text-slate-400">→</span>
                  {friendlyStatus(h.to, t)}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Scheduling dialog (create) */}
      <SchedulingDialog
        open={schedOpen}
        onOpenChange={setSchedOpen}
        ticket={ticket}
        t={t}
        onLinked={handleLinked}
      />

      {/* Unlink confirmation */}
      <AlertDialog open={unlinkOpen} onOpenChange={(o) => !busy && setUnlinkOpen(o)}>
        <AlertDialogContent data-testid="racket-lab-tickets-detail-scheduling-unlink-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("racketLab.tickets.schedulingUnlinkConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("racketLab.tickets.schedulingUnlinkConfirmBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={busy}
              data-testid="racket-lab-tickets-detail-scheduling-unlink-cancel"
            >
              {t("racketLab.tickets.schedulingCancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnlink}
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="racket-lab-tickets-detail-scheduling-unlink-confirm"
            >
              {t("racketLab.tickets.schedulingUnlink")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
